import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Sparkles, Zap, Film, ArrowRight, Star, TrendingUp, Users, LogIn, PlayCircle, Heart, Wand2, Palette, Layers } from 'lucide-react';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // 检查登录状态
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      setIsLoggedIn(!!token);
    };
    
    // 初始检查
    checkAuth();
    
    // 监听localStorage变化（跨标签页同步）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        checkAuth();
      }
    };
    
    // 监听自定义事件（同标签页登录后触发）
    const handleLocalStorageChange = (e: Event) => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleLocalStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleLocalStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const features = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: 'AI 智能生成',
      description: '使用先进的 AI 技术，将文字转化为震撼的视频内容',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: '快速高效',
      description: '几秒钟生成专业级视频，无需专业技能',
      color: 'from-yellow-500 to-orange-500',
    },
    {
      icon: <Film className="w-8 h-8" />,
      title: '无水印下载',
      description: '支持多平台视频无水印下载，高质量输出',
      color: 'from-blue-500 to-cyan-500',
    },
  ];

  const stats = [
    { label: 'AI视频制作', value: '10K+', icon: <Play className="w-5 h-5" /> },
    { label: '用户数', value: '5K+', icon: <Users className="w-5 h-5" /> },
    { label: '满意度', value: '98%', icon: <Star className="w-5 h-5" /> },
    { label: '增长率', value: '200%', icon: <TrendingUp className="w-5 h-5" /> },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-yellow-200 via-orange-200 via-pink-200 via-purple-200 via-rose-200 to-pink-300">
      {/* 动态背景 */}
      <div className="fixed inset-0 z-0">
        {/* 主渐变背景 - 多层次渐变 */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-200/95 via-orange-200/95 via-pink-200/95 via-purple-200/95 via-rose-200/95 to-pink-300/95" />
        
        {/* 第二层渐变 - 增加深度 */}
        <div className="absolute inset-0 bg-gradient-to-tr from-yellow-300/30 via-transparent via-pink-300/30 to-purple-400/30" />
        
        {/* 第三层渐变 - 对角线渐变 */}
        <div className="absolute inset-0 bg-gradient-to-bl from-orange-300/20 via-transparent to-purple-300/20" />
        
        {/* 动态光效 - 跟随鼠标 */}
        <div
          className="absolute inset-0 opacity-40 transition-opacity duration-1000"
          style={{
            background: `radial-gradient(circle 600px at ${mousePosition.x}% ${mousePosition.y}%, rgba(251, 146, 60, 0.25) 0%, rgba(236, 72, 153, 0.2) 30%, transparent 60%)`,
          }}
        />
        
        {/* 浮动光球 */}
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full blur-3xl animate-float"
              style={{
                width: `${200 + i * 50}px`,
                height: `${200 + i * 50}px`,
                background: `radial-gradient(circle, ${i % 2 === 0 ? 'rgba(251, 146, 60, 0.15)' : 'rgba(236, 72, 153, 0.15)'} 0%, transparent 70%)`,
                left: `${(i * 15) % 80}%`,
                top: `${(i * 25) % 70}%`,
                animationDelay: `${i * 2}s`,
                animationDuration: `${15 + i * 3}s`,
              }}
            />
          ))}
        </div>
        
        {/* 网格背景 - 更明显 */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(251, 146, 60, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(236, 72, 153, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridMove 25s linear infinite',
          }}
        />
        
        {/* 粒子效果 - 更多彩 */}
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 40 }).map((_, i) => {
            const colors = [
              'from-orange-300 to-pink-300',
              'from-pink-300 to-purple-300',
              'from-purple-300 to-pink-300',
              'from-yellow-300 to-orange-300',
            ];
            return (
              <div
                key={i}
                className={`absolute rounded-full bg-gradient-to-r ${colors[i % colors.length]} opacity-40 animate-pulse`}
                style={{
                  width: `${Math.random() * 5 + 2}px`,
                  height: `${Math.random() * 5 + 2}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                }}
              />
            );
          })}
        </div>
      </div>

      {/* 主要内容 */}
      <div className="relative z-10">
        {/* 顶部导航栏 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-md border-b border-pink-200/30 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/2025.png" alt="Logo" className="w-10 h-10 object-contain" />
              <span className="text-pink-800 font-bold text-xl">猫爪AI工作社</span>
            </div>
            <div className="flex items-center space-x-4">
              {isLoggedIn ? (
                <>
                  <button
                    onClick={() => {
                      console.log('Home: 点击控制台按钮...');
                      const token = localStorage.getItem('access_token');
                      console.log('Home: 检查token - exists:', !!token, 'Length:', token ? token.length : 0);

                      if (!token) {
                        console.error('Home: token不存在，无法导航到/app');
                        alert('请先登录！');
                        return;
                      }

                      console.log('Home: token存在，开始导航到/app...');
                      
                      // 确保token确实保存了
                      const finalCheck = localStorage.getItem('access_token');
                      console.log('Home: 最终检查token - exists:', !!finalCheck, 'Length:', finalCheck ? finalCheck.length : 0);
                      
                      if (!finalCheck) {
                        console.error('Home: token在导航前丢失了！');
                        alert('Token丢失，请重新登录！');
                        return;
                      }
                      
                      // 强制刷新页面以确保状态正确
                      console.log('Home: 开始跳转到/app...');
                      window.location.href = '/app';
                    }}
                    className="px-4 py-2 text-pink-700 hover:text-pink-500 transition-colors"
                  >
                    控制台
                  </button>
                  <button
                    onClick={() => {
                      localStorage.removeItem('access_token');
                      window.location.reload();
                    }}
                    className="px-4 py-2 text-pink-700 hover:text-pink-500 transition-colors"
                  >
                    退出
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-pink-700 hover:text-pink-500 transition-colors flex items-center gap-2"
                  >
                    <LogIn className="w-4 h-4" />
                    登录
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full hover:shadow-lg transition-all duration-300 font-semibold"
                  >
                    免费注册
                  </button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-4 py-12 pt-20">
          <div className="max-w-7xl mx-auto w-full text-center relative">

            {/* Logo 和标题 */}
            <div className="mb-6 animate-fade-in">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <img
                    src="/2025.png"
                    alt="Logo"
                    className="w-20 h-20 md:w-28 md:h-28 object-contain animate-bounce-slow"
                    style={{
                      filter: 'drop-shadow(0 0 25px rgba(236, 72, 153, 0.6))',
                    }}
                  />
                  {/* 可爱的装饰星星 */}
                  <div className="absolute -top-2 -left-2 text-2xl animate-spin-slow">⭐</div>
                  <div className="absolute -top-2 -right-2 text-xl animate-pulse">✨</div>
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-lg animate-bounce-slow" style={{ animationDelay: '0.5s' }}>💫</div>
                </div>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-3 animate-slide-up">
                <span className="bg-gradient-to-r from-orange-500 via-pink-500 via-purple-500 to-pink-600 bg-clip-text text-transparent animate-gradient">
                  猫爪AI工作社
                </span>
                <span className="ml-3 text-3xl md:text-5xl animate-bounce-slow">🐾</span>
              </h1>
              <p className="text-lg md:text-xl text-pink-700 mb-6 animate-slide-up-delay font-medium">
                ✨ 让创意变为现实，用 AI 创造无限可能 ✨
              </p>
            </div>

            {/* CTA 按钮 */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-12 animate-fade-in-delay">
              {isLoggedIn ? (
                <>
                  <button
                    onClick={() => navigate('/app/generate')}
                    className="group relative px-6 py-3 bg-gradient-to-r from-pink-400 via-purple-400 to-pink-500 text-white rounded-full font-semibold text-base shadow-xl hover:shadow-pink-400/50 transform hover:scale-110 transition-all duration-300 overflow-hidden border-2 border-pink-300/50"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <span>🎨</span>
                      开始创作
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => {
                      const token = localStorage.getItem('access_token');
                      console.log('Home: 点击进入控制台 - Token exists:', !!token, 'Token length:', token ? token.length : 0, 'Token预览:', token ? token.substring(0, 30) + '...' : 'null');
                      if (!token) {
                        console.warn('Home: 点击进入控制台时没有token！');
                        alert('请先登录！');
                        return;
                      }
                      console.log('Home: 使用navigate跳转到/app（保持token）...');
                      // 使用navigate而不是window.location.href，避免页面刷新导致状态丢失
                      navigate('/app');
                    }}
                    className="px-6 py-3 bg-white/60 backdrop-blur-sm text-pink-700 rounded-full font-semibold text-base border-2 border-pink-300/50 hover:bg-white/80 hover:border-pink-400/70 transition-all duration-300 transform hover:scale-105 shadow-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span>📊</span>
                      进入控制台
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/app/generate')}
                    className="group relative px-6 py-3 bg-gradient-to-r from-pink-400 via-purple-400 to-pink-500 text-white rounded-full font-semibold text-base shadow-xl hover:shadow-pink-400/50 transform hover:scale-110 transition-all duration-300 overflow-hidden border-2 border-pink-300/50"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <span>🚀</span>
                      立即开始
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-6 py-3 bg-white/60 backdrop-blur-sm text-pink-700 rounded-full font-semibold text-base border-2 border-pink-300/50 hover:bg-white/80 hover:border-pink-400/70 transition-all duration-300 transform hover:scale-105 shadow-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span>💖</span>
                      免费注册
                    </span>
                  </button>
                </>
              )}
            </div>

            {/* 视频展示区域 - 带统计数据 */}
            <div className="mb-16 animate-fade-in-delay-2 w-full relative flex items-center justify-center gap-6 px-4">
              {/* 左侧统计数据 - 错位叠加 */}
              <div className="hidden lg:flex flex-col gap-0 relative z-10">
                {stats.slice(0, 2).map((stat, index) => (
                  <div
                    key={index}
                    className="group bg-white/60 backdrop-blur-sm rounded-2xl p-3 border-2 border-pink-200/50 hover:border-pink-300/70 hover:bg-white/80 transition-all duration-300 w-28 relative shadow-sm"
                    style={{
                      transform: `translateX(${index * 12}px) translateY(${index * 16}px) scale(1)`,
                      zIndex: `${10 - index}`,
                      marginLeft: `${index * 8}px`,
                      marginTop: `${index * 10}px`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = `translateX(${index * 12}px) translateY(${index * 16}px) scale(1.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = `translateX(${index * 12}px) translateY(${index * 16}px) scale(1)`;
                    }}
                  >
                    <div className="flex items-center justify-center mb-1 text-pink-500 group-hover:text-pink-600 transition-colors">
                      {stat.icon}
                    </div>
                    <div className="text-xl font-bold text-pink-700 mb-1 text-center">{stat.value}</div>
                    <div className="text-xs text-pink-600 text-center">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* 视频容器 - 缩小2格 */}
              <div className="relative group rounded-3xl overflow-hidden border-4 border-pink-400/60 shadow-2xl shadow-pink-400/40 transform hover:scale-[1.02] transition-all duration-500 max-w-2xl w-full flex-shrink-0">
                  {/* 视频背景装饰 */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-blue-600/20 animate-pulse" />
                  
                  {/* 视频占位或实际视频 */}
                  <div className="relative aspect-video bg-gradient-to-br from-purple-800/50 to-pink-800/50 backdrop-blur-sm flex items-center justify-center">
                    {/* 播放按钮 */}
                    <button
                      className="group/play relative w-24 h-24 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center shadow-2xl hover:shadow-pink-500/50 transform hover:scale-110 transition-all duration-300"
                      onClick={() => {
                        // 可以打开视频或跳转到示例
                        const videoElement = document.getElementById('hero-video') as HTMLVideoElement;
                        if (videoElement) {
                          videoElement.play();
                        }
                      }}
                    >
                      <PlayCircle className="w-12 h-12 text-white ml-1 group-hover/play:scale-110 transition-transform" />
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover/play:opacity-100 transition-opacity animate-ping" />
                    </button>
                    
                    {/* 视频元素（可以使用实际视频或占位） */}
                    <video
                      id="hero-video"
                      className="hidden w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      preload="auto"
                    >
                      {/* 如果有可能的视频文件，可以在这里添加 */}
                      <source src="/demo-video.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    
                    {/* 如果没有视频文件，显示动画背景 */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="grid grid-cols-4 gap-3 md:gap-4 p-6 md:p-8">
                        {Array.from({ length: 12 }).map((_, i) => (
                          <div
                            key={i}
                            className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-purple-400/40 to-pink-400/40 rounded-xl animate-pulse"
                            style={{
                              animationDelay: `${i * 0.1}s`,
                              animationDuration: '2s',
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* 视频标题 */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">AI 视频制作示例</h3>
                    <p className="text-gray-300 text-sm">体验 AI 创造的视觉魔法</p>
                  </div>
                  
                  {/* 光效动画 */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  
                  {/* 装饰性元素 */}
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-pink-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>

              {/* 右侧统计数据 - 错位叠加 */}
              <div className="hidden lg:flex flex-col gap-0 relative z-10">
                {stats.slice(2, 4).map((stat, index) => (
                  <div
                    key={index + 2}
                    className="group bg-white/60 backdrop-blur-sm rounded-2xl p-3 border-2 border-pink-200/50 hover:border-pink-300/70 hover:bg-white/80 transition-all duration-300 w-28 relative shadow-sm"
                    style={{
                      transform: `translateX(${index * -12}px) translateY(${index * 16}px) scale(1)`,
                      zIndex: `${10 - index}`,
                      marginRight: `${index * 8}px`,
                      marginTop: `${index * 10}px`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = `translateX(${index * -12}px) translateY(${index * 16}px) scale(1.1)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = `translateX(${index * -12}px) translateY(${index * 16}px) scale(1)`;
                    }}
                  >
                    <div className="flex items-center justify-center mb-1 text-pink-500 group-hover:text-pink-600 transition-colors">
                      {stat.icon}
                    </div>
                    <div className="text-xl font-bold text-pink-700 mb-1 text-center">{stat.value}</div>
                    <div className="text-xs text-pink-600 text-center">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* 移动端统计数据 - 显示在视频下方 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 lg:hidden animate-fade-in-delay-2">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="group bg-white/60 backdrop-blur-sm rounded-2xl p-3 border-2 border-pink-200/50 hover:border-pink-300/70 hover:bg-white/80 transition-all duration-300 hover:scale-110 transform shadow-sm"
                >
                  <div className="flex items-center justify-center mb-1 text-pink-500 group-hover:text-pink-600 transition-colors">
                    {stat.icon}
                  </div>
                  <div className="text-xl font-bold text-pink-700 mb-1 text-center">{stat.value}</div>
                  <div className="text-xs text-pink-600 text-center">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 动画展示区域 */}
        <section className="relative py-12 px-4 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            {/* 功能展示卡片 */}
            <div className="mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
                <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent animate-gradient">
                  核心功能
                </span>
                <span className="ml-2 animate-bounce-slow">🎯</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { 
                    title: '文字生成视频', 
                    icon: <Heart className="w-16 h-16" />, 
                    color: 'from-purple-500 to-pink-500',
                    description: '只需输入文字描述，AI 就能为你生成精美视频',
                    emoji: '✨'
                  },
                  { 
                    title: '图片转视频', 
                    icon: <Wand2 className="w-16 h-16" />, 
                    color: 'from-orange-500 to-pink-500',
                    description: '将静态图片转换为生动的动态视频',
                    emoji: '🎨'
                  },
                  { 
                    title: '视频风格转换', 
                    icon: <Palette className="w-16 h-16" />, 
                    color: 'from-yellow-400 to-orange-500',
                    description: '轻松转换视频风格，创意无限',
                    emoji: '🌈'
                  },
                  { 
                    title: '批量处理', 
                    icon: <Layers className="w-16 h-16" />, 
                    color: 'from-pink-500 to-purple-500',
                    description: '高效批量处理，提升创作效率',
                    emoji: '⚡'
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-5 border-2 border-white/50 hover:border-pink-300/70 transition-all duration-500 hover:scale-110 hover:shadow-2xl hover:shadow-pink-400/40 overflow-hidden"
                  >
                    {/* 背景光效 */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-15 transition-opacity duration-500 rounded-3xl`} />
                    
                    {/* 顶部装饰光晕 */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-pink-400/30 to-purple-400/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
                    
                    {/* 图标容器 */}
                    <div className="relative mb-4">
                      <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${item.color} text-white transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                        {item.icon}
                      </div>
                      {/* 装饰性emoji */}
                      <div className="absolute -top-1 -right-1 text-3xl animate-bounce-slow" style={{ animationDelay: `${index * 0.2}s` }}>
                        {item.emoji}
                      </div>
                    </div>
                    
                    {/* 标题和描述 */}
                    <h3 className="text-xl font-bold text-pink-700 mb-2 relative z-10 group-hover:text-pink-800 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-pink-600 text-xs leading-relaxed relative z-10 group-hover:text-pink-700 transition-colors">
                      {item.description}
                    </p>
                    
                    {/* 底部装饰线条 */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${item.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-b-3xl`} />
                    
                    {/* 光效扫过 */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-200/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">
              <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent animate-gradient">
                强大功能
              </span>
              <span className="ml-2 animate-bounce-slow">💪</span>
            </h2>
            <p className="text-base md:text-lg text-center text-pink-700 mb-10 font-medium">
              ✨ 一站式视频创作解决方案 ✨
            </p>
            
            <div className="grid md:grid-cols-3 gap-5">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-pink-200/50 hover:border-purple-400/70 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-purple-300/30"
                >
                  {/* 背景渐变 */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`}
                  />
                  
                  {/* 内容 */}
                  <div className="relative z-10">
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${feature.color} mb-4 text-white transform group-hover:rotate-6 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-pink-700 mb-3">{feature.title}</h3>
                    <p className="text-pink-600">{feature.description}</p>
                  </div>

                  {/* 光效 */}
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-12 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-pink-200/60 via-purple-200/60 to-pink-200/60 backdrop-blur-sm rounded-3xl p-8 border-2 border-pink-300/50 shadow-xl">
              <h2 className="text-3xl md:text-4xl font-bold text-pink-800 mb-4">
                <span>准备好开始创作了吗？</span>
                <span className="ml-2">🎉</span>
              </h2>
              <p className="text-lg text-pink-700 mb-6">
                💕 加入数千名创作者，用 AI 释放你的创造力 💕
              </p>
              <button
                onClick={() => navigate(isLoggedIn ? '/app/generate' : '/register')}
                className="group relative px-8 py-4 bg-gradient-to-r from-pink-400 via-purple-400 to-pink-500 text-white rounded-full font-semibold text-base shadow-xl hover:shadow-pink-400/50 transform hover:scale-110 transition-all duration-300 overflow-hidden border-2 border-pink-300/50"
              >
                <span className="relative z-10 flex items-center gap-2 justify-center">
                  <span>{isLoggedIn ? '🎨' : '💖'}</span>
                  {isLoggedIn ? '开始创作' : '免费注册'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* CSS 动画 */}
      <style>{`
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        
        .animate-slide-up-delay {
          animation: slide-up 0.8s ease-out 0.2s both;
        }
        
        .animate-fade-in-delay {
          animation: fade-in 1s ease-out 0.4s both;
        }
        
        .animate-fade-in-delay-2 {
          animation: fade-in 1s ease-out 0.6s both;
        }
        
        .animate-bounce-slow {
          animation: bounce 3s ease-in-out infinite;
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        
        .animate-float {
          animation: float 20s ease-in-out infinite;
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 5s ease infinite;
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default Home;

